import { ExternalLink } from "lucide-react";

/**
 * A smiling-sun-shaped Amazon buy button, matching the golden sun on the
 * "Sun, Shine So Bright" book cover.
 *
 * Composition:
 *  - Outer wavy corona of long/short rays that spin slowly
 *  - Inner glowing sun disc with a warm gradient
 *  - Sleepy, happy closed eyes with lashes
 *  - Soft rosy cheeks + gentle smile (little curl at the corners)
 *  - Little butterflies + hibiscus dots as ambient sparkles
 *  - "Amazon" label sits inside the sun; whole thing lifts + brightens on hover
 */
export default function SunButton({ href, label = "Amazon" }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Buy on ${label}`}
      className="sun-btn"
      data-testid="book-buy-amazon"
    >
      <svg
        viewBox="0 0 220 220"
        width="200"
        height="200"
        role="img"
        aria-hidden="true"
      >
        <defs>
          {/* Warm sun-disc gradient — sherbet through hot marigold */}
          <radialGradient id="sunDisc" cx="45%" cy="40%" r="65%">
            <stop offset="0%" stopColor="#FFF6C4" />
            <stop offset="45%" stopColor="#FFD84A" />
            <stop offset="85%" stopColor="#FFA020" />
            <stop offset="100%" stopColor="#E5761A" />
          </radialGradient>
          {/* Ray gradient — bright center → deeper amber tip */}
          <radialGradient id="sunRay" cx="50%" cy="50%" r="55%">
            <stop offset="0%" stopColor="#FFEA8C" />
            <stop offset="70%" stopColor="#FFB230" />
            <stop offset="100%" stopColor="#F08417" />
          </radialGradient>
          {/* Glow that halos the whole disc */}
          <radialGradient id="sunGlow" cx="50%" cy="50%" r="55%">
            <stop offset="0%" stopColor="#FFF8B0" stopOpacity="0.75" />
            <stop offset="100%" stopColor="#FFA020" stopOpacity="0" />
          </radialGradient>
          <filter id="sunSoftShadow" x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow dx="0" dy="6" stdDeviation="6" floodColor="#B85E12" floodOpacity="0.32" />
          </filter>
          <filter id="raysGlow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="0.6" />
          </filter>
        </defs>

        {/* Ambient tropical sparkles (butterflies + petals) */}
        <g fill="#FF7AA2" opacity="0.55">
          <circle cx="24" cy="30" r="2.4" />
          <circle cx="198" cy="28" r="2" />
          <circle cx="16" cy="188" r="1.8" />
          <circle cx="204" cy="184" r="2.2" />
        </g>
        <g fill="#FFFFFF" opacity="0.9">
          <circle cx="34" cy="18" r="1.2" />
          <circle cx="190" cy="46" r="1.4" />
          <circle cx="46" cy="200" r="1.2" />
          <circle cx="180" cy="204" r="1.4" />
        </g>

        {/* SOFT HALO GLOW */}
        <circle cx="110" cy="110" r="100" fill="url(#sunGlow)" />

        {/* SPINNING RAY GROUP (12 rays, alternating long/short) */}
        <g className="sun-rays" filter="url(#raysGlow)">
          {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((deg, i) => {
            const long = i % 2 === 0;
            const halfW = long ? 8 : 6;
            const outer = long ? 12 : 32;
            const inner = long ? 62 : 68;
            return (
              <g key={deg} transform={`rotate(${deg} 110 110)`}>
                <path
                  d={`M ${110 - halfW} ${inner}
                      L 110 ${outer}
                      L ${110 + halfW} ${inner}
                      Z`}
                  fill="url(#sunRay)"
                />
              </g>
            );
          })}
        </g>

        {/* MAIN SUN DISC */}
        <g filter="url(#sunSoftShadow)">
          <circle cx="110" cy="110" r="52" fill="url(#sunDisc)" />
        </g>

        {/* Subtle inner highlight to add roundness */}
        <ellipse cx="94" cy="92" rx="22" ry="12" fill="#FFF3B8" opacity="0.55" />

        {/* Closed happy eyes with lashes */}
        <g stroke="#7A3B04" strokeWidth="2.4" fill="none" strokeLinecap="round">
          <path d="M 86 104 Q 93 98 100 104" />
          <path d="M 120 104 Q 127 98 134 104" />
          {/* Lashes */}
          <path d="M 86 102 L 83 98" strokeWidth="1.6" />
          <path d="M 93 99 L 92 95" strokeWidth="1.6" />
          <path d="M 100 102 L 103 98" strokeWidth="1.6" />
          <path d="M 120 102 L 117 98" strokeWidth="1.6" />
          <path d="M 127 99 L 128 95" strokeWidth="1.6" />
          <path d="M 134 102 L 137 98" strokeWidth="1.6" />
        </g>

        {/* Rosy sunlit cheeks */}
        <ellipse cx="85" cy="122" rx="8" ry="5" fill="#FF9EAA" opacity="0.7" />
        <ellipse cx="135" cy="122" rx="8" ry="5" fill="#FF9EAA" opacity="0.7" />

        {/* Gentle smile with tiny lip curls */}
        <path
          d="M 92 128 Q 110 144 128 128"
          stroke="#7A3B04"
          strokeWidth="2.6"
          fill="none"
          strokeLinecap="round"
        />
        <path d="M 92 128 Q 89 126 88 130" stroke="#7A3B04" strokeWidth="2.2" fill="none" strokeLinecap="round" />
        <path d="M 128 128 Q 131 126 132 130" stroke="#7A3B04" strokeWidth="2.2" fill="none" strokeLinecap="round" />

        {/* Label — resting inside the sun */}
        <text
          x="110"
          y="164"
          textAnchor="middle"
          fontFamily="'Fredoka', sans-serif"
          fontSize="20"
          fontWeight="700"
          fill="#7A3B04"
          style={{ paintOrder: "stroke", stroke: "#FFF6C4", strokeWidth: 0.6 }}
        >
          {label}
        </text>
        <text
          x="110"
          y="180"
          textAnchor="middle"
          fontFamily="'Fredoka', sans-serif"
          fontSize="10"
          fontWeight="600"
          fill="#7A3B04"
          opacity="0.9"
        >
          shine on &rarr;
        </text>
      </svg>
      <span className="sun-btn-external" aria-hidden="true">
        <ExternalLink size={12} />
      </span>
    </a>
  );
}
